import {
  ChangeEvent,
  FocusEvent,
  FormEvent,
  KeyboardEvent,
  MouseEvent,
  forwardRef,
} from 'react';

import GoButton, { GoButtonVariants } from './GoButton';

const PromptInput = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  {
    className?: string;
    onClick?: (e: MouseEvent<HTMLFormElement>) => void;
    onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
    onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onFocus?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onKeyDown?: (
      e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    placeholder?: string;
    prompt?: string;
    active?: boolean;
    loading?: boolean;
    disabled?: boolean;
    buttonText?: string;
    buttonVariant?: GoButtonVariants;
    datadogActionName?: string;
    textarea?: boolean;
    hideCursor?: boolean;
  }
>(
  (
    {
      className,
      onClick,
      onSubmit,
      onChange,
      onFocus,
      onKeyDown,
      placeholder,
      prompt = '',
      active,
      loading,
      disabled,
      buttonText,
      buttonVariant,
      datadogActionName,
      textarea,
      hideCursor,
    },
    ref,
  ) => {
    const handleKeyDown = (
      e: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
    ) => {
      // Call custom onKeyDown handler first if provided
      if (onKeyDown) {
        onKeyDown(e);
      }

      // Only handle Enter if it's a textarea and not prevented by custom handler
      if (textarea && e.key === 'Enter' && !e.shiftKey && !e.defaultPrevented) {
        e.preventDefault();
        // Trigger form submission by creating a synthetic form event
        const form = e.currentTarget.closest('form');
        if (form && onSubmit) {
          const syntheticEvent = new Event('submit', {
            bubbles: true,
            cancelable: true,
          });
          Object.defineProperty(syntheticEvent, 'target', { value: form });
          Object.defineProperty(syntheticEvent, 'currentTarget', {
            value: form,
          });
          onSubmit(syntheticEvent as unknown as FormEvent<HTMLFormElement>);
        }
      }
      // Shift+Enter will naturally create a new line, so we don't need to handle it explicitly
    };
    return (
      <form
        className={`dark:bg-gray-90 z-2 pointer-events-auto relative flex w-full flex-none items-center rounded-2xl bg-white transition-all ${
          className
        }`}
        onClick={onClick}
        onSubmit={onSubmit}
      >
        {textarea ? (
          <textarea
            ref={ref as React.Ref<HTMLTextAreaElement>}
            id="prompt"
            autoComplete="off"
            autoFocus
            disabled={disabled}
            className="font-display outline-hidden h-[154px] w-full resize-none rounded-[inherit] bg-transparent pl-3 pr-24 pt-4 text-base sm:pl-4 dark:text-white"
            style={{ caretColor: hideCursor ? 'transparent' : undefined }}
            onChange={onChange}
            onFocus={onFocus}
            onKeyDown={handleKeyDown}
            value={prompt}
          />
        ) : (
          <input
            ref={ref as React.Ref<HTMLInputElement>}
            id="prompt"
            autoComplete="off"
            autoFocus
            disabled={disabled}
            className="font-display outline-hidden h-[52px] w-full rounded-[inherit] bg-transparent pl-3 pr-24 text-base sm:pl-4 dark:text-white"
            style={{ caretColor: hideCursor ? 'transparent' : undefined }}
            onChange={onChange}
            onFocus={onFocus}
            onKeyDown={handleKeyDown}
            type="text"
            value={prompt}
          />
        )}
        {prompt?.trim() === '' && (
          <div
            className={`font-display text-gray-45 dark:text-gray-60 pointer-events-none absolute left-3 right-24 top-0 flex h-[52px] items-center text-base text-opacity-30 sm:left-4`}
          >
            <div className="truncate">{placeholder}</div>
          </div>
        )}
        <GoButton
          active={active}
          variant={buttonVariant}
          className="absolute bottom-1.5 right-2"
          data-dd-action-name={datadogActionName}
          loading={loading}
          disabled={disabled}
          borderRadius="full"
          type="submit"
        >
          {buttonText}
        </GoButton>
      </form>
    );
  },
);

PromptInput.displayName = 'PromptInput';

export default PromptInput;
